// =============================================================================
// Services Tab - Gestione Servizi (Ricette)
// =============================================================================

import { useState } from 'react';
import { Plus, Edit2, Trash2, Clock, DollarSign, Package, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useServices, useDeleteService } from '../../api/hooks';
import { ServiceDefinition } from '../../types';
import { ServiceFormWizard } from './ServiceFormWizard';
import {
  Badge,
  Button,
  EmptyState,
  ErrorBlock,
  IconButton,
  LoadingBlock,
  SettingsCard,
  TabPageHeader,
} from './_shared';

export function ServicesTab() {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [editingService, setEditingService] = useState<ServiceDefinition | null>(null);
  const { t } = useTranslation('settings');

  const { data: services, isLoading, error } = useServices();
  const deleteService = useDeleteService();

  const handleCreate = () => {
    setEditingService(null);
    setIsWizardOpen(true);
  };

  const handleEdit = (service: ServiceDefinition) => {
    setEditingService(service);
    setIsWizardOpen(true);
  };

  const handleDelete = async (service: ServiceDefinition) => {
    if (window.confirm(t('services.deleteConfirm', { name: service.name }))) {
      try {
        await deleteService.mutateAsync(service.id);
      } catch (error) {
        console.error('Error deleting service:', error);
        alert(t('services.deleteError'));
      }
    }
  };

  const handleWizardClose = () => {
    setIsWizardOpen(false);
    setEditingService(null);
  };

  // Format duration
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  // Format price
  const formatPrice = (price: number | null): string => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
  };

  if (isLoading) {
    return <LoadingBlock />;
  }

  if (error) {
    return <ErrorBlock message={t('services.loadError')} />;
  }

  return (
    <div>
      <TabPageHeader
        title={t('services.title')}
        description={`${services?.length ?? 0} ${t('services.count')}`}
        action={
          <Button icon={Plus} onClick={handleCreate}>
            {t('services.create')}
          </Button>
        }
      />

      {services?.length === 0 ? (
        <SettingsCard>
          <EmptyState
            icon={Package}
            title={t('services.emptyTitle')}
            description={t('services.emptyMessage')}
            action={
              <Button icon={Plus} onClick={handleCreate}>
                {t('services.createFirst')}
              </Button>
            }
          />
        </SettingsCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services?.map((service) => (
            <SettingsCard key={service.id} className="!p-5">
              {/* Header card */}
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                    style={{ backgroundColor: service.color || '#3B82F6' }}
                  />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{service.name}</h3>
                    <p className="text-xs text-gray-500 truncate font-mono">{service.code}</p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <IconButton
                    icon={Edit2}
                    tone="primary"
                    label={t('common:buttons.edit')}
                    onClick={() => handleEdit(service)}
                  />
                  <IconButton
                    icon={Trash2}
                    tone="danger"
                    label={t('common:buttons.delete')}
                    onClick={() => handleDelete(service)}
                    disabled={deleteService.isPending}
                  />
                </div>
              </div>

              {service.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">
                  {service.description}
                </p>
              )}

              <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{formatDuration(service.durationMinutes)}</span>
                </div>
                {service.basePrice !== null && (
                  <>
                    <span className="text-gray-300">·</span>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5" />
                      <span>{formatPrice(service.basePrice)}</span>
                    </div>
                  </>
                )}
                {service.maxClients > 1 && (
                  <>
                    <span className="text-gray-300">·</span>
                    <div className="flex items-center gap-1" title={t('services.maxClients')}>
                      <Users className="w-3.5 h-3.5" />
                      <span>{t('services.maxClientsBadge', { count: service.maxClients })}</span>
                    </div>
                  </>
                )}
              </div>

              {service.category && (
                <Badge tone="gray" className="mb-2">
                  {service.category}
                </Badge>
              )}

              {service.requirements && service.requirements.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    {t('services.requirements')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {service.requirements.map((req, idx) => (
                      <Badge key={idx} tone="emerald">
                        {req.quantity}× {req.resourceType.name || req.resourceType.code}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <Badge tone={service.isActive ? 'green' : 'gray'}>
                  {service.isActive ? t('common:status.enabled') : t('common:status.disabled')}
                </Badge>
                {service.isPublic && (
                  <span className="text-xs text-gray-400 font-medium">{t('common:status.public')}</span>
                )}
              </div>
            </SettingsCard>
          ))}
        </div>
      )}

      {/* Wizard Modal */}
      {isWizardOpen && (
        <ServiceFormWizard
          service={editingService}
          onClose={handleWizardClose}
        />
      )}
    </div>
  );
}
